import {
  Box,
  Heading,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import UploadIcon from '../../../assets/icons/upload.svg';
import * as XLSX from 'xlsx';
import moment from 'moment';
import {
  useCreateTaskMutation,
  useGetWorkflowByIdQuery,
} from '../../../services';
import { FormFieldTypes, NewTask } from '../../../models';
import { useEffect, useState } from 'react';
import { CenteredSpinner } from '../../../components';

export const NewTaskUsingExcel = ({
  isOpen,
  onClose,
  workflowId,
}: {
  isOpen: boolean;
  onClose: () => void;
  workflowId: number;
}) => {
  const [tasksUploadStatus, setTasksUploadStatus] = useState<{
    count: number;
    done: number;
  }>({ count: 0, done: 0 });
  const [loading, setLoading] = useState<boolean>(false);
  const { data: workflowData, isLoading } = useGetWorkflowByIdQuery(
    workflowId,
    {
      skip: !workflowId,
    }
  );

  const [createTask, { isLoading: isCreateTaskLoading }] =
    useCreateTaskMutation();

  const excelSerialDateToDate = (serial: number): string => {
    const excelEpoch = moment('1899-12-30');
    const finalDate = excelEpoch.add(serial, 'days').toDate();
    return moment(finalDate).format('YYYY-MM-DD');
  };

  const processImportedData = async (data: any[]) => {
    setLoading(true);
    data.forEach(taskRow => {
      let formAnswersList: NewTask['formAnswers'] = [];
      Object.entries(taskRow).forEach(([key, value]) => {
        const targetField = workflowData?.data.form.fields.find(
          field => field.title === key
        );

        if (targetField) {
          formAnswersList.push({
            fieldId: targetField?.id,
            answer:
              targetField.type === FormFieldTypes.date
                ? excelSerialDateToDate(value)
                : String(value).trim(),
          });
        }
      });

      setTasksUploadStatus(prev => ({
        count: data.length,
        done: prev.done + 1,
      }));

      createTask({
        workflowId: workflowId,
        isChildTask: false,
        formAnswers: formAnswersList,
      }).then(() => {
        console.log('');
      });
    });

    setLoading(false);
    onClose();
  };

  return (
    <Modal
      blockScrollOnMount={false}
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
    >
      <ModalOverlay />

      <ModalContent pb="4">
        <ModalHeader>
          <Heading fontSize="md" fontWeight="semibold" color="custom_blue.1">
            Create Tasks Using Excel
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box w="full">
            {loading ? (
              <CenteredSpinner />
            ) : (
              <>
                <Input
                  display="none"
                  id="files"
                  onChange={e => {
                    const files = (e.target as HTMLInputElement).files;
                    if (!files) return;

                    const transformedFiles = Array.from(files);

                    transformedFiles.forEach(file => {
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = event => {
                          const data = new Uint8Array(
                            event.target?.result as ArrayBuffer
                          );
                          const workbook = XLSX.read(data, { type: 'array' });

                          // Get the first sheet
                          const worksheet =
                            workbook.Sheets[workbook.SheetNames[0]];

                          // Convert sheet to JSON
                          const jsonData = XLSX.utils.sheet_to_json(worksheet);

                          processImportedData(jsonData);
                        };
                        reader.readAsArrayBuffer(file);
                      }
                    });
                  }}
                  type="file"
                  multiple={false}
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                />

                <label htmlFor="files">
                  <VStack
                    cursor="pointer"
                    border="2px"
                    borderRadius="md"
                    borderStyle="dashed"
                    borderColor="custom_orange.2"
                    py="3"
                  >
                    <Box
                      border="1px"
                      borderColor="custom_gray.4"
                      borderRadius="md"
                      p="2"
                    >
                      <Image src={UploadIcon} />
                    </Box>

                    <Text color="custom_blue.1" fontSize="sm">
                      <Box
                        as="span"
                        color="custom_orange.2"
                        fontWeight="semibold"
                      >
                        Click to upload
                      </Box>{' '}
                      or drag and drop
                    </Text>
                    <Text color="custom_blue.1" fontSize="xs">
                      XLSX or CSV
                    </Text>
                  </VStack>
                </label>
              </>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
